import { DonutChart, type DonutChartProps as MantineDonutChartProps } from '@mantine/charts';
import { Box, Group, Stack, Text } from '@mantine/core';
import { cva, type VariantProps } from 'class-variance-authority';
import styles from './DonutChartWithLegend.module.css';

export interface DonutChartDataItem {
    name: string;
    value: number;
    color: string;
}

interface ExtraProps {
    label: string;
    displayedTotalOverride?: number | string | null;
    chartSize?: number;
    data: DonutChartDataItem[];
}

export type DonutChartWithLegendProps = ExtraProps & Omit<MantineDonutChartProps, 'data'>;

// CVA size variants
const legendValueText = cva(styles.legendValueText, {
    variants: {
        size: {
            sm: styles.legendValueTextSm,
            md: '',
            lg: styles.legendValueTextLg,
        },
    },
});

const legendNameText = cva(styles.legendNameText, {
    variants: {
        size: {
            sm: styles.legendNameTextSm,
            md: '',
            lg: styles.legendNameTextLg,
        },
    },
});

const centerLabelTitle = cva(styles.centerLabelTitle, {
    variants: {
        size: {
            sm: styles.centerLabelTitleSm,
            md: '',
            lg: styles.centerLabelTitleLg,
        },
    },
});

const centerLabelSubtitle = cva(styles.centerLabelSubtitle, {
    variants: {
        size: {
            sm: styles.centerLabelSubtitleSm,
            md: '',
            lg: styles.centerLabelSubtitleLg,
        },
    },
});

type SizeVariant = VariantProps<typeof legendValueText>['size'];

function getSizeVariant(chartSize: number): SizeVariant {
    if (chartSize <= 150) return 'sm';
    if (chartSize > 300) return 'lg';
    return 'md';
}

export function DonutChartWithLegend({
    data,
    label,
    chartSize = 200,
    displayedTotalOverride,
    withTooltip = false,
    ...mantineProps
}: DonutChartWithLegendProps) {
    if (!data || data.length === 0) {
        return <Text>No data available</Text>;
    }

    const calculatedTotal = data.reduce((sum, d) => sum + d.value, 0);

    const totalDisplayed =
        typeof displayedTotalOverride === 'string'
            ? displayedTotalOverride
            : (displayedTotalOverride ?? calculatedTotal);

    const sortedData = [...data].sort((a, b) => b.value - a.value);

    const size = getSizeVariant(chartSize);
    const chartSizeRem = `${chartSize / 16}rem`;

    const formatNumber = (value: number | string) =>
        typeof value === 'number' ? new Intl.NumberFormat().format(value) : value;

    return (
        <Group
            className={styles.wrapper}
            role="figure"
            aria-label={`${label} chart with legend`}
            wrap="nowrap"
        >
            <Box
                className={styles.chartContainer}
                style={{ width: chartSizeRem, height: chartSizeRem }}
                role="img"
                aria-label={`${label} donut chart showing ${data.length} segments`}
            >
                <DonutChart data={data} size={chartSize} withTooltip={withTooltip} {...mantineProps} />
                <Box
                    className={styles.centerLabel}
                    role="status"
                    aria-label={`Total ${label}: ${formatNumber(totalDisplayed)}`}
                >
                    <Text className={centerLabelTitle({ size })}>{formatNumber(totalDisplayed)}</Text>
                    <Text className={centerLabelSubtitle({ size })}>{label}</Text>
                </Box>
            </Box>
            <Stack
                gap={0}
                justify="center"
                className={styles.legendStack}
                role="list"
                aria-label={`${label} breakdown legend`}
            >
                {sortedData.map(item => {
                    const percent =
                        calculatedTotal > 0 ? Math.round((item.value / calculatedTotal) * 100) : 0;

                    return (
                        <Group key={item.name} className={styles.legendItem} role="listitem">
                            <Box
                                className={styles.colorBox}
                                bg={item.color}
                                title={item.name}
                                role="presentation"
                            />
                            <Box className={styles.legendTextRow}>
                                <Text
                                    className={legendValueText({ size })}
                                    title={`${item.value} (${percent}%)`}
                                    aria-label={`Value: ${formatNumber(item.value)} which is ${percent} percent`}
                                >
                                    {formatNumber(item.value)} ({percent}%)
                                </Text>
                                <Text
                                    className={legendNameText({ size })}
                                    title={item.name}
                                    aria-label={`Category: ${item.name}`}
                                >
                                    {item.name}
                                </Text>
                            </Box>
                        </Group>
                    );
                })}
            </Stack>
        </Group>
    );
}
